import React, { useEffect, useMemo, useReducer } from "react";
import { Button, Input, message, Tooltip } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { useReactiveVar } from "@apollo/client";

import useEffectiveLanguage from "components/use-hooks/use-effective-language";
import { localizeIt } from "components/service/localize";
import TranslateLanguageIcon from "components/layout/icons/translate-language-icon";
import { translationParamsVar } from "graphql/cache";

import { hasTranslationApiAccess } from "./translation-ai-client";
import { requestInputTranslation } from "./dynamic-translation";

import "./translated-textarea-control.scss";

const TARGET_LANGUAGE = "de";
const passthroughLanguages = [ "en", "de" ];
const cyrillicTextPattern = /[\u0400-\u04FF]/;
const sourceLanguageTextPatterns = {
    pl : /[\u0104\u0105\u0106\u0107\u0118\u0119\u0141\u0142\u0143\u0144\u015A\u015B\u0179\u017A\u017B\u017C]/,
    cs : /[\u010C\u010D\u010E\u010F\u011A\u011B\u0147\u0148\u0158\u0159\u0160\u0161\u0164\u0165\u016E\u016F\u017D\u017E]/,
    sk : /[\u010C\u010D\u010E\u010F\u0139\u013A\u013D\u013E\u0147\u0148\u00D4\u00F4\u0154\u0155\u0160\u0161\u0164\u0165\u017D\u017E]/,
    hu : /[\u0150\u0151\u0170\u0171]/,
    ro : /[\u0102\u0103\u00C2\u00E2\u00CE\u00EE\u0218\u0219\u021A\u021B]/,
    hr : /[\u010C\u010D\u0106\u0107\u0110\u0111\u0160\u0161\u017D\u017E]/,
    bs : /[\u010C\u010D\u0106\u0107\u0110\u0111\u0160\u0161\u017D\u017E]/,
    sl : /[\u010C\u010D\u0160\u0161\u017D\u017E]/,
    sq : /[\u00CB\u00EB\u00C7\u00E7]/
};

const getTextValue = (value) => String(value || "");

const hasSourceScriptText = (value, language) => {
    const text = getTextValue(value);
    const sourcePattern = sourceLanguageTextPatterns[language];

    return cyrillicTextPattern.test(text) || !!(sourcePattern && sourcePattern.test(text));
};

const getInitialState = () => ({
    submitText        : "",
    shouldBlockSubmit : false,
    required          : false,
    ready             : true,
    status            : "idle",
    viewMode          : "de",
    dirty             : false,
    canTranslate      : false,
    language          : "",
    targetLang        : TARGET_LANGUAGE
});

const createState = ({
        value,
        initialTranslatedValue,
        canTranslate
    }) => {
    const initialDeText = getTextValue(initialTranslatedValue);
    const visibleValue = initialDeText || getTextValue(value);

    if (!canTranslate) {
        return {
            deText      : visibleValue,
            nativeText  : "",
            visibleText : visibleValue,
            viewMode    : "de",
            deDirty     : false,
            nativeDirty : false,
            status      : "idle"
        };
    }

    if (initialDeText) {
        return {
            deText      : initialDeText,
            nativeText  : null,
            visibleText : initialDeText,
            viewMode    : "de",
            deDirty     : false,
            nativeDirty : false,
            status      : "idle"
        };
    }

    return {
        deText      : "",
        nativeText  : visibleValue,
        visibleText : visibleValue,
        viewMode    : "native",
        deDirty     : false,
        nativeDirty : !!visibleValue.trim(),
        status      : visibleValue.trim() ? "needs_translation" : "idle"
    };
};

const reducer = (state, action) => {
    switch (action.type) {
        case "reset":
            return createState(action.payload);

        case "change":
            if (!action.canTranslate) {
                return {
                    ...state,
                    deText      : action.text,
                    visibleText : action.text,
                    deDirty     : false,
                    nativeDirty : false,
                    status      : "idle"
                };
            }

            if (state.viewMode === "native") {
                return {
                    ...state,
                    nativeText  : action.text,
                    visibleText : action.text,
                    deDirty     : false,
                    nativeDirty : !!action.text.trim(),
                    status      : action.text.trim() ? "needs_translation" : "idle"
                };
            }

            const deDirty = hasSourceScriptText(action.text, action.language);

            return {
                ...state,
                deText      : action.text,
                nativeText  : null,
                visibleText : action.text,
                deDirty,
                nativeDirty : false,
                status      : deDirty ? "needs_translation" : "idle"
            };

        case "loading":
            return {
                ...state,
                status: "loading"
            };

        case "show_native":
            return {
                ...state,
                visibleText : state.nativeText || "",
                viewMode    : "native",
                deDirty     : false,
                nativeDirty : false,
                status      : "idle"
            };

        case "show_de":
            return {
                ...state,
                visibleText : state.deText,
                viewMode    : "de",
                deDirty     : false,
                nativeDirty : false,
                status      : "idle"
            };

        case "translated_to_native":
            return {
                ...state,
                deText      : action.deText,
                nativeText  : action.nativeText,
                visibleText : action.nativeText,
                viewMode    : "native",
                deDirty     : false,
                nativeDirty : false,
                status      : "idle"
            };

        case "translated_to_de":
            return {
                ...state,
                deText      : action.deText,
                nativeText  : action.nativeText,
                visibleText : action.deText,
                viewMode    : "de",
                deDirty     : false,
                nativeDirty : false,
                status      : "translated"
            };

        case "error":
            return {
                ...state,
                status: state.nativeDirty || state.deDirty ? "needs_translation" : "idle"
            };

        default:
            return state;
    }
};

const getTranslationState = ({
        state,
        canTranslate,
        language
    }) => {
    const dirty = (state.viewMode === "native" && state.nativeDirty) ||
        (state.viewMode === "de" && state.deDirty);
    const needsGermanView = state.viewMode === "native";
    const shouldBlockSubmit = canTranslate &&
        !!state.visibleText.trim() &&
        (state.status === "loading" || dirty || needsGermanView);

    return {
        submitText        : canTranslate ? state.deText : state.visibleText,
        shouldBlockSubmit,
        required          : shouldBlockSubmit,
        ready             : !shouldBlockSubmit,
        status            : state.status,
        viewMode          : state.viewMode,
        dirty             : dirty || needsGermanView,
        translatedFromText: state.viewMode === "native" ? state.deText : (state.nativeText || ""),
        canTranslate,
        language,
        targetLang        : TARGET_LANGUAGE
    };
};

const TranslatedTextareaControl = React.forwardRef(({
        value,
        onChange,
        onTranslationStateChange,
        initialTranslatedValue = "",
        namespace = "input",
        context = "",
        className = "",
        resetKey = "",
        ...props
    }, ref) => {
    const { language } = useEffectiveLanguage();
    const translationParams = useReactiveVar(translationParamsVar);
    const shouldTranslate = !!language && passthroughLanguages.indexOf(language) === -1;
    const canTranslate = shouldTranslate && hasTranslationApiAccess(translationParams);
    const initialPayload = {
        value,
        initialTranslatedValue,
        canTranslate
    };
    const [ state, dispatch ] = useReducer(reducer, initialPayload, createState);

    const translationState = useMemo(() => getTranslationState({
        state,
        canTranslate,
        language
    }), [ state, canTranslate, language ]);

    const setInputValue = (nextValue) => {
        if (!onChange) return;

        onChange({
            target: {
                value: nextValue
            }
        });
    };

    useEffect(() => {
        dispatch({
            type    : "reset",
            payload : {
                value,
                initialTranslatedValue,
                canTranslate
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ resetKey, initialTranslatedValue, canTranslate, language ]);

    useEffect(() => {
        if (!onTranslationStateChange) return;

        onTranslationStateChange(translationState);
    }, [ onTranslationStateChange, translationState ]);

    const handleChange = (e) => {
        const nextText = getTextValue(e?.target?.value);

        dispatch({
            type: "change",
            text: nextText,
            canTranslate,
            language
        });

        setInputValue(nextText);
    };

    const handleTranslate = async () => {
        const currentText = getTextValue(state.visibleText);

        if (!canTranslate || !currentText.trim() || state.status === "loading") return;

        if (state.viewMode === "de" && state.deDirty) {
            dispatch({ type: "loading" });

            try {
                const translatedText = await requestInputTranslation({
                    sourceLang : language,
                    targetLang : TARGET_LANGUAGE,
                    namespace,
                    context,
                    text       : currentText
                });

                dispatch({
                    type       : "translated_to_de",
                    deText     : translatedText,
                    nativeText : null
                });
                setInputValue(translatedText);
            } catch (e) {
                dispatch({ type: "error" });
                message.error(localizeIt("TRANSLATION.Text_InputTranslationError"));
            }

            return;
        }

        if (state.viewMode === "de" && state.nativeText !== null) {
            dispatch({ type: "show_native" });
            setInputValue(state.nativeText || "");
            return;
        }

        if (state.viewMode === "native" && !state.nativeDirty) {
            dispatch({ type: "show_de" });
            setInputValue(state.deText);
            return;
        }

        dispatch({ type: "loading" });

        try {
            if (state.viewMode === "native") {
                const translatedText = await requestInputTranslation({
                    sourceLang : language,
                    targetLang : TARGET_LANGUAGE,
                    namespace,
                    context,
                    text       : currentText
                });

                dispatch({
                    type       : "translated_to_de",
                    deText     : translatedText,
                    nativeText : currentText
                });
                setInputValue(translatedText);
                return;
            }

            const translatedText = await requestInputTranslation({
                sourceLang : TARGET_LANGUAGE,
                targetLang : language,
                namespace,
                context,
                text       : currentText
            });

            dispatch({
                type       : "translated_to_native",
                deText     : currentText,
                nativeText : translatedText
            });
            setInputValue(translatedText);
        } catch (e) {
            dispatch({ type: "error" });
            message.error(localizeIt("TRANSLATION.Text_InputTranslationError"));
        }
    };

    const tooltipText = state.viewMode === "native" ?
        (state.nativeDirty ?
            localizeIt("TRANSLATION.Button_Text_TranslateInput") :
            localizeIt("TRANSLATION.Button_Text_ShowOriginal")
        ) :
        (state.deDirty ?
            localizeIt("TRANSLATION.Button_Text_TranslateInput") :
            localizeIt("TRANSLATION.Button_Text_TranslateNative")
        );

    return (
        <div className={ `translated-textarea-control ${ canTranslate ? "with-translation" : "" } ${ className }` }>
            <Input.TextArea
                { ...props }
                ref={ ref }
                value={ state.visibleText }
                disabled={ props.disabled || state.status === "loading" }
                onChange={ handleChange }
            />
            { canTranslate &&
                <Tooltip title={ tooltipText }>
                    <span className={ `translated-textarea-button-wrap status-${ state.status }` }>
                        <Button
                            type="text"
                            className="translated-textarea-button"
                            icon={ <TranslateLanguageIcon /> }
                            loading={ state.status === "loading" }
                            disabled={ !state.visibleText.trim() || state.status === "loading" }
                            onClick={ handleTranslate }
                            aria-label={ tooltipText }
                        />
                    </span>
                </Tooltip>
            }
            { canTranslate && state.status !== "idle" &&
                <div className={ `translated-textarea-status ${ state.status }` }>
                    { state.status === "translated" &&
                        <>
                            <CheckOutlined />
                            <span>{ localizeIt("TRANSLATION.Text_InputTranslated") }</span>
                        </>
                    }
                    { state.status === "loading" &&
                        <span>{ localizeIt("TRANSLATION.Text_InputTranslating") }</span>
                    }
                    { state.status === "needs_translation" &&
                        <span>{ localizeIt("TRANSLATION.Text_InputTranslationRequired") }</span>
                    }
                    { state.status === "error" &&
                        <span>{ localizeIt("TRANSLATION.Text_InputTranslationError") }</span>
                    }
                </div>
            }
        </div>
    );
});

TranslatedTextareaControl.defaultProps = {
    onTranslationStateChange: null
};

export const getInitialTextareaTranslationState = getInitialState;

export default TranslatedTextareaControl;
